import {
  diffOfferContent,
  diffTextLines,
  OFFER_TEXT_FIELDS,
  type OfferContentField,
  type OfferVersionDto,
} from '@fos/shared/contracts/offers';
import { OFFER_FIELD_LABELS } from '@fos/shared/labels';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { Mono } from '../../../ui/Mono';

const TEXT = new Set<OfferContentField>(OFFER_TEXT_FIELDS);

/**
 * Porównanie dwóch wersji oferty (historia wersji): zmienione sekcje linia po linii (dodane na zielono, usunięte
 * na czerwono, przekreślone), zmienione liczby „przed → po”, lista sekcji bez zmian. Bez „Widzi stawki i kwoty”
 * stawki nie ma w porównaniu.
 */
export function VersionCompare({
  before,
  after,
  seesRates,
}: {
  before: OfferVersionDto;
  after: OfferVersionDto;
  seesRates: boolean;
}) {
  const fields = diffOfferContent(before.content, after.content, { seesRates });
  const changed = fields.filter((f) => f.changed);
  const same = fields.filter((f) => !f.changed && (f.before || f.after));
  return (
    <section aria-label={`Zmiany: wersja ${before.versionNo} → ${after.versionNo}`} className="flex flex-col gap-5">
      <p className="text-body-s text-fg-secondary">
        Zmiany z wersji {before.versionNo} na {after.versionNo}
        {changed.length === 0 ? ': treść bez zmian.' : '.'}
      </p>
      {changed.map((f) => (
        <div key={f.field} className="flex flex-col gap-1.5">
          <h3 className="text-caption font-medium text-fg-muted">{OFFER_FIELD_LABELS[f.field]}</h3>
          {TEXT.has(f.field) ? (
            <div className="overflow-hidden rounded-lg border border-line text-body-s">
              {diffTextLines(f.before, f.after).map((line, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: kolejność linii jest stała
                  key={i}
                  className={cn(
                    'flex gap-2 px-3 py-0.5 break-words whitespace-pre-wrap',
                    line.kind === 'added' && 'bg-success-subtle text-fg',
                    line.kind === 'removed' && 'bg-danger-subtle text-fg-secondary line-through',
                    line.kind === 'same' && 'text-fg-secondary',
                  )}
                >
                  <span aria-hidden className="w-3 shrink-0 font-mono text-fg-muted">
                    {line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ''}
                  </span>
                  <span className="sr-only">
                    {line.kind === 'added' ? 'Dodano: ' : line.kind === 'removed' ? 'Usunięto: ' : ''}
                  </span>
                  <span className="min-w-0">{line.text || ' '}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="flex flex-wrap items-center gap-2 text-body-s">
              <Mono className="text-fg-muted line-through">{f.before ?? '—'}</Mono>
              <ArrowRight size={14} strokeWidth={1.75} aria-label="na" className="text-fg-muted" />
              <Mono className="text-fg">{f.after ?? '—'}</Mono>
            </p>
          )}
        </div>
      ))}
      {same.length > 0 && (
        <p className="text-caption text-fg-muted">
          Bez zmian: {same.map((f) => OFFER_FIELD_LABELS[f.field].toLocaleLowerCase('pl-PL')).join(', ')}.
        </p>
      )}
    </section>
  );
}
